/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// USE ESTIMATE BUDGET RATES HOOK
// ============================================================================
// Hook to fetch and cache budget rates for an estimate

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import type { BudgetRates } from "@/lib/business-logic/estimates";
import {
  QUERY_GC_TIME,
  QUERY_RETRY,
  QUERY_STALE_TIME,
  getRetryDelay,
} from "@/lib/constants/query-config";
import { logger } from "@/lib/utils/logger";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface UseEstimateBudgetRatesOptions {
  estimateId: string | null;
  budgetId?: string | null | undefined;
  enabled?: boolean;
}

interface UseEstimateBudgetRatesReturn {
  budgetRates: BudgetRates | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch budget rates for an estimate
 * Uses React Query with 5-minute staleTime for caching
 */
export function useEstimateBudgetRates({
  estimateId,
  budgetId,
  enabled = true,
}: UseEstimateBudgetRatesOptions): UseEstimateBudgetRatesReturn {
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled: organizationReady } =
    useOrganizationQueryGuard({
      enabled,
      debugKey: "useEstimateBudgetRates",
    });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  const queryKey = useMemo(
    () => ["estimate-budget-rates", estimateId, budgetId, organizationId],
    [estimateId, budgetId, organizationId],
  );
  const { data, isLoading, error } = useQuery<BudgetRates | null>({
    queryKey,
    queryFn: async (): Promise<BudgetRates | null> => {
      if (!estimateId) {
        return null;
      }

      try {
        const res = await api.get<{ budgetRates?: BudgetRates | null }>(
          `/api/estimates/${estimateId}/budget-rates`,
        );

        if (isApiFailure(res)) {
          // Don't show toast for missing budget (expected case)
          if (res.code !== "RESOURCE_NOT_FOUND") {
          }
          logger.info("No budget rates found for estimate", {
            estimateId,
            code: res.code,
          });
          return null;
        }

        return res.data?.budgetRates ?? null;
      } catch (err) {
        logger.error("Failed to fetch budget rates", {
          error: err,
          estimateId,
        });
        // Return null on error - components will use defaults
        return null;
      }
    },
    enabled: organizationReady && Boolean(estimateId),
    staleTime: QUERY_STALE_TIME.STANDARD, // budget rates are STANDARD
    gcTime: QUERY_GC_TIME.STANDARD,
    retry: QUERY_RETRY.ONCE, // Only retry once on failure
    retryDelay: getRetryDelay,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    budgetRates: data ?? null,
    isLoading,
    error: error instanceof Error ? error : null,
  };
}
